import React from "react";
import { Form, Input, Select, Drawer, Button, Typography, Space } from "antd";
import { notify } from "@/shared/utils/notify";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { citiesService, storesService } from "../../../shared/services";
import { DataCities } from "../../../redux/@types/cities";
import { ListAutoComplet } from "../../modalAddress/style";
import { StoreLocationMap } from "./StoreLocationMap";
import { StoreScheduleEditor } from "./StoreScheduleEditor";
import {
  encodeSchedule,
  defaultScheduleBlocks,
  validateSchedulePayload,
} from "../lib/storeSchedule";
import { StoreShippingRatesPanel } from "../../shippingRates/ui/StoreShippingRatesPanel";

const { Text, Title } = Typography;

/** Centro por defecto (Colombia) para sesgar autocompletado */
const DEFAULT_MAP_BIAS = { lat: 4.6097, lng: -74.0817 };

type Props = {
  openModalCreate: boolean;
  setOpenModalCreate: (v: boolean) => void;
  openModalEdit: boolean;
  setOpenModalEdit: (v: boolean) => void;
  storeId?: number;
  onSuccess?: () => void;
};

export const ModalStores = ({
  openModalCreate,
  setOpenModalCreate,
  openModalEdit,
  setOpenModalEdit,
  storeId,
  onSuccess,
}: Props) => {
  const [form] = Form.useForm();
  const [cities, setCities] = React.useState<DataCities[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [geo, setGeo] = React.useState<{ lat: number; lng: number } | null>(
    null
  );
  /** Solo para remontar el mapa al cambiar dirección (no en cada arrastre del pin). */
  const [mapNonce, setMapNonce] = React.useState(0);

  const open = openModalCreate || openModalEdit;

  const loadCities = React.useCallback(async () => {
    const res = await citiesService.find({
      query: { $limit: 5000, $sort: { name: 1 } },
    });
    const data = "data" in res ? res.data : [];
    const list = Array.isArray(data) ? data : [];
    setCities(list);
    return list;
  }, []);

  React.useEffect(() => {
    if (openModalCreate) {
      loadCities().catch(() => notify.error("No se pudieron cargar las ciudades"));
    }
  }, [openModalCreate, loadCities]);

  const cityLabel = (cityId: number | undefined, list: DataCities[] = cities) => {
    if (!cityId) return "";
    const c = list.find((x) => Number(x.id) === Number(cityId));
    if (!c) return "";
    return c.state?.name ? `${c.name}, ${c.state.name}` : c.name;
  };

  const buildGeocodeQuery = (
    address: string,
    cityId?: number,
    list?: DataCities[],
  ) => {
    const parts = [
      address?.trim(),
      cityLabel(cityId, list),
      "Colombia",
    ].filter(Boolean);
    return parts.join(", ");
  };

  async function cityLabelFromApi(cityId: number | undefined) {
    if (!cityId) return "";
    try {
      const c = await citiesService.get(cityId);
      if (!c) return "";
      return c.state?.name ? `${c.name}, ${c.state.name}` : c.name;
    } catch {
      return "";
    }
  }

  const geocodeAndSetPin = async (address: string, cityId?: number) => {
    const q = buildGeocodeQuery(address, cityId);
    if (!address?.trim()) {
      notify.warning("Escriba una dirección");
      return;
    }
    try {
      const results = await geocodeByAddress(q);
      const ll = await getLatLng(results[0]);
      setGeo({ lat: ll.lat, lng: ll.lng });
      form.setFieldsValue({
        address: results[0].formatted_address ?? address,
      });
      setMapNonce((n) => n + 1);
    } catch {
      notify.error("No se pudo ubicar esa dirección en el mapa");
    }
  };

  React.useEffect(() => {
    if (!openModalEdit || !storeId) return;
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        // Cargar ciudades antes de setear el form, si no el Select muestra solo el id
        const [row, cityList] = await Promise.all([
          storesService.get(storeId),
          loadCities(),
        ]);
        if (cancelled) return;

        const cityId =
          row.location_city_id != null
            ? Number(row.location_city_id)
            : undefined;

        // Si la ciudad no vino en el listado, la agregamos para que el Select muestre el nombre
        if (
          cityId &&
          !cityList.some((c) => Number(c.id) === cityId)
        ) {
          try {
            const missing = await citiesService.get(cityId);
            if (missing && !cancelled) {
              setCities((prev) => [...prev, missing]);
            }
          } catch {
            // ignore
          }
        }

        form.setFieldsValue({
          name: row.name,
          location_city_id: cityId,
          phone: row.phone,
          phone2: row.phone2,
          email: row.email,
          schedule: row.schedule,
          address: row.address,
          is_pick_and_go: row.is_pick_and_go ?? "no",
        });

        const lat = row.latitude != null ? Number(row.latitude) : NaN;
        const lng = row.longitude != null ? Number(row.longitude) : NaN;
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          setGeo({ lat, lng });
          setMapNonce((n) => n + 1);
        } else if (row.address) {
          try {
            const cityPart = await cityLabelFromApi(cityId);
            const q = [row.address.trim(), cityPart, "Colombia"]
              .filter(Boolean)
              .join(", ");
            const results = await geocodeByAddress(q);
            const ll = await getLatLng(results[0]);
            if (!cancelled) {
              setGeo({ lat: ll.lat, lng: ll.lng });
              setMapNonce((n) => n + 1);
            }
          } catch {
            if (!cancelled) setGeo(null);
          }
        } else {
          setGeo(null);
        }
      } catch {
        if (!cancelled) notify.error("No se pudo cargar la sede");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [openModalEdit, storeId, form, loadCities]);

  React.useEffect(() => {
    if (openModalCreate) {
      form.resetFields();
      form.setFieldsValue({
        is_pick_and_go: "no",
        schedule: encodeSchedule(defaultScheduleBlocks()),
      });
      setGeo(null);
      setMapNonce(0);
    }
  }, [openModalCreate, form]);

  const handleClose = () => {
    form.resetFields();
    setGeo(null);
    setMapNonce(0);
    setOpenModalCreate(false);
    setOpenModalEdit(false);
  };

  const addressWatch = Form.useWatch("address", form) ?? "";
  const cityWatch = Form.useWatch("location_city_id", form);

  const onFinish = async (values: Record<string, unknown>) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        latitude: geo?.lat ?? null,
        longitude: geo?.lng ?? null,
      };
      if (openModalEdit && storeId) {
        await storesService.patch(storeId, payload);
        notify.success("Sede actualizada");
      } else {
        await storesService.create(payload);
        notify.success("Sede creada");
      }
      onSuccess?.();
      handleClose();
    } catch (e: unknown) {
      const err = e as { message?: string };
      notify.error(err?.message ?? "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceSelect = async (selectedAddress: string) => {
    try {
      const results = await geocodeByAddress(selectedAddress);
      const ll = await getLatLng(results[0]);
      const formatted =
        results[0].formatted_address ?? selectedAddress;
      form.setFieldsValue({ address: formatted });
      setGeo({ lat: ll.lat, lng: ll.lng });
      setMapNonce((n) => n + 1);
    } catch {
      notify.error("No se pudo obtener las coordenadas");
    }
  };

  return (
    <Drawer
      title={openModalEdit ? "Editar sede" : "Nueva sede"}
      placement="right"
      width={openModalEdit && storeId ? 720 : 520}
      open={open}
      onClose={handleClose}
      destroyOnClose
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            Guardar
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ is_pick_and_go: "no" }}
      >
        <Form.Item
          name="name"
          label="Nombre"
          rules={[{ required: true, message: "Requerido" }]}
        >
          <Input placeholder="Ej. Sushi2Go Centro" />
        </Form.Item>
        <Form.Item
          name="location_city_id"
          label="Ciudad"
          rules={[{ required: true, message: "Requerido" }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Seleccione ciudad"
            loading={loading && cities.length === 0}
            options={cities.map((c) => ({
              value: Number(c.id),
              label: `${c.name}${c.state?.name ? ` — ${c.state.name}` : ""}`,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="address"
          hidden
          rules={[{ required: true, message: "Indique la dirección" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item label="Dirección" required>
          <Text type="secondary" style={{ display: "block", marginBottom: 6 }}>
            Busque con Google o escriba la dirección y pulse «Ubicar en mapa».
          </Text>
          <PlacesAutocomplete
            value={addressWatch}
            onChange={(v) => form.setFieldsValue({ address: v })}
            onSelect={handlePlaceSelect}
            searchOptions={
              typeof window !== "undefined" &&
              (window as unknown as { google?: { maps: { LatLng: new (lat: number, lng: number) => object } } })
                .google
                ? {
                    location: new window.google.maps.LatLng(
                      DEFAULT_MAP_BIAS.lat,
                      DEFAULT_MAP_BIAS.lng
                    ),
                    radius: 280_000,
                    componentRestrictions: { country: "co" },
                  }
                : { componentRestrictions: { country: "co" } }
            }
          >
            {({
              getInputProps,
              suggestions,
              getSuggestionItemProps,
              loading: placesLoading,
            }) => (
              <div style={{ position: "relative" }}>
                <Input
                  {...getInputProps({
                    placeholder: "Dirección de la sede",
                  })}
                />
                {placesLoading && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Buscando…
                  </Text>
                )}
                <div className="autocomplete-dropdown-container">
                  {suggestions.map((suggestion, index) => (
                    <ListAutoComplet
                      className="items"
                      {...getSuggestionItemProps(suggestion)}
                      key={index}
                    >
                      <div
                        className="items_address"
                        style={{ background: "white" }}
                      >
                        <div>
                          <p
                            style={{
                              cursor: "pointer",
                              fontWeight: 600,
                              margin: 0,
                            }}
                          >
                            {suggestion.formattedSuggestion.mainText}
                          </p>
                          <p style={{ cursor: "pointer", margin: 0 }}>
                            {suggestion.formattedSuggestion.secondaryText}
                          </p>
                        </div>
                        <span>{`>`}</span>
                      </div>
                    </ListAutoComplet>
                  ))}
                </div>
              </div>
            )}
          </PlacesAutocomplete>
          <Space style={{ marginTop: 8 }} wrap>
            <Button
              size="small"
              onClick={() =>
                geocodeAndSetPin(
                  String(form.getFieldValue("address") ?? ""),
                  cityWatch
                )
              }
            >
              Ubicar en mapa
            </Button>
          </Space>
        </Form.Item>

        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            Ubicación en el mapa
          </Text>
          {geo ? (
            <StoreLocationMap
              key={mapNonce}
              center={geo}
              onMarkerDragEnd={(lat, lng) => setGeo({ lat, lng })}
            />
          ) : (
            <div
              style={{
                height: 220,
                background: "#fafafa",
                borderRadius: 8,
                border: "1px dashed #d9d9d9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#8c8c8c",
                fontSize: 13,
                textAlign: "center",
                padding: 16,
              }}
            >
              Elija una sugerencia de dirección o use «Ubicar en mapa» para ver
              el pin.
            </div>
          )}
        </div>

        <Form.Item
          name="phone"
          label="Teléfono"
          rules={[{ required: true, message: "Requerido" }]}
        >
          <Input placeholder="Teléfono principal" />
        </Form.Item>
        <Form.Item name="phone2" label="Teléfono 2">
          <Input />
        </Form.Item>
        <Form.Item name="email" label="Correo">
          <Input type="email" />
        </Form.Item>
        <Form.Item
          name="schedule"
          label="Horario"
          rules={[
            {
              validator: async (_, v) => {
                const err = validateSchedulePayload(
                  typeof v === "string" ? v : ""
                );
                if (err) throw new Error(err);
              },
            },
          ]}
        >
          <StoreScheduleEditor />
        </Form.Item>
        <Form.Item name="is_pick_and_go" label="Recogida en tienda">
          <Select
            options={[
              { value: "no", label: "No" },
              { value: "si", label: "Sí" },
            ]}
          />
        </Form.Item>
      </Form>

      {openModalEdit && storeId ? (
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
          <Title level={5} style={{ marginBottom: 12 }}>
            Domicilio de esta sede
          </Title>
          <StoreShippingRatesPanel storeId={storeId} compact />
        </div>
      ) : null}
    </Drawer>
  );
};
